import ModelTypeConstants from 'constants/ModelTypeConstants';
import {getDefaultSearchTypes} from 'nav/sidepanel/listing/SearchSearchParams';
import APIUtil from 'util/APIUtil';
import isModelTypeEnabledForListing from 'util/isModelTypeEnabledForListing';
import {typeToName} from 'util/NameUtil';

export const RECENT_ITEMS_LIMIT = 5;
export const RECENT_SEARCHES_LIMIT = 10;

// openedObjectType sentinel marking a recent search as an AI chat (openedApiId holds the chat id).
export const RECENT_SEARCH_CHAT_OBJECT_TYPE = 'chat';

// Accent color for AI chat affordances (the wand icon and its labels).
export const AI_CHAT_ACCENT_COLOR = '#8b5cf6';

// Group name for streamed search results. Retained as a semantic value so CLICKED_RESULT analytics can
// report which group a clicked result belonged to, but its visual section header is intentionally not
// rendered in Omnibox (per Figma — search results sit directly under the input with no header).
export const SEARCH_RESULTS_GROUP = 'Search Results';

// Unlike SEARCH_RESULTS_GROUP, this section's header IS rendered, to set file matches apart.
export const CONTENT_MATCHES_GROUP = 'Content matches';

// The direct answer sits at the very top, just under the query.
export const ANSWER_GROUP = 'Answer';

// AI suggestion sections. These render below the search results so the fast search results stay
// directly under the input.
export const ASK_BENCHLING_GROUP = 'Ask Benchling';
export const HELP_DOCS_GROUP = 'Help Docs';

// The advanced-search escalation row leads the list in its own headerless section, above the saved
// searches and streamed results. A distinct group (rather than sharing SEARCH_RESULTS_GROUP) keeps it
// separated from the streamed results, which now render below the saved searches.
export const ADVANCED_SEARCH_GROUP = 'Advanced search';

// Saved searches matching the query are pinned above the streamed search results in their own
// labeled section.
export const SAVED_SEARCHES_GROUP = 'Saved searches';

// Cap on how many name-matched saved searches are surfaced.
export const MAX_SAVED_SEARCH_ACTIONS = 5;

export const MAX_RESULT_ACTIONS = 20;

// The "Content matches" (attachment) section has its own budget so it can't be crowded out by search
// results. A visibility floor, not rank fusion — replaced by unified backend retrieval with RRF.
export const MAX_ATTACHMENT_ACTIONS = 5;

// Type-toggle filter tabs (Entry / Entity). Each maps to one or more backend
// OmniboxObjectType strings (see benchling/llm/omnibox/omnibox_object_types.py). These ids
// become the `objectTypes` sent to search. "Entity" spans the entity family; the backend
// TypesFilter auto-expands "Sequence" into its DNA/RNA + oligo subtypes.
export type TypeToggleId = 'entry' | 'entity';

const TYPE_TOGGLE_OBJECT_TYPES: Record<TypeToggleId, ReadonlyArray<{id: string; name: string}>> = {
  entry: [{id: 'Entry', name: 'Entry'}],
  entity: [
    {id: 'Custom Entity', name: 'Custom Entity'},
    {id: 'Sequence', name: 'Sequence'},
    {id: 'Protein', name: 'Protein'},
    {id: 'Mixture', name: 'Mixture'},
    {id: 'Molecule', name: 'Molecule'},
  ],
};

export function getTypeToggleFilters(toggle: TypeToggleId): Array<{kind: 'type'; id: string; name: string}> {
  return TYPE_TOGGLE_OBJECT_TYPES[toggle].map((t) => ({kind: 'type', id: t.id, name: t.name}));
}

// Maps a type toggle to the ModelTypeConstants used by the full search page's TYPES filter (see
// ALL_LISTABLE_TYPES in search/SearchConstants). This is a separate vocabulary from the backend
// OmniboxObjectType strings above: "entity" enumerates the entity family (the omnibox "Sequence"
// object type expands to its DNA/RNA + oligo subtypes here) so the search page restores an
// equivalent filter when opened from the omnibox.
const TYPE_TOGGLE_MODEL_TYPES: Record<TypeToggleId, ReadonlyArray<ModelTypeConstants>> = {
  entry: [ModelTypeConstants.ENTRY],
  entity: [
    ModelTypeConstants.BASIC_FOLDER_ITEM,
    ModelTypeConstants.SEQUENCE,
    ModelTypeConstants.RNA_SEQUENCE,
    ModelTypeConstants.DNA_OLIGO,
    ModelTypeConstants.RNA_OLIGO,
    ModelTypeConstants.PROTEIN,
    ModelTypeConstants.ANTIBODY,
    ModelTypeConstants.OLIGO_CONJUGATE,
    ModelTypeConstants.OLIGO_DUPLEX,
    ModelTypeConstants.MIXTURE,
    ModelTypeConstants.MOLECULE,
  ],
};

export function getTypeToggleModelTypes(toggle: TypeToggleId): ReadonlyArray<ModelTypeConstants> {
  return TYPE_TOGGLE_MODEL_TYPES[toggle];
}

// Types the omnibox jumps to directly that global search doesn't list by default. Projects (sources)
// are not among them: like global search, the omnibox finds a project through its root folder, which
// shares its name, so a project would otherwise appear twice.
const OMNIBOX_ONLY_SEARCH_TYPES: ReadonlyArray<ModelTypeConstants> = [ModelTypeConstants.PROCESS_OUTPUT];

// The types the omnibox may search, sent to the backend as `allowedTypes`. Built on global search's
// default types so both apply the same feature and capability gating; type toggles and schema
// filters only narrow within this set.
export function getOmniboxAllowedTypes(): Array<string> {
  return [
    ...getDefaultSearchTypes(),
    ...OMNIBOX_ONLY_SEARCH_TYPES.filter((type) => isModelTypeEnabledForListing(type)),
  ];
}

// Maps ModelTypeConstants enum values to display names for API-ID-based resolution.
// See also DOMAIN_CLASS_TO_DISPLAY_NAME in OmniboxAction.tsx which maps backend domain
// graph class names (PascalCase) to display names for search result normalization.
const MODEL_TYPE_DISPLAY_NAMES: Partial<Record<ModelTypeConstants, string>> = {
  [ModelTypeConstants.ENTRY]: 'Entry',
  [ModelTypeConstants.SEQUENCE]: 'DNA Sequence',
  [ModelTypeConstants.RNA_SEQUENCE]: 'RNA Sequence',
  [ModelTypeConstants.DNA_OLIGO]: 'DNA Oligo',
  [ModelTypeConstants.RNA_OLIGO]: 'RNA Oligo',
  [ModelTypeConstants.PROTEIN]: 'Protein',
  [ModelTypeConstants.BASIC_FOLDER_ITEM]: 'Custom Entity',
  [ModelTypeConstants.MIXTURE]: 'Mixture',
  [ModelTypeConstants.MOLECULE]: 'Molecule',
  [ModelTypeConstants.ANTIBODY]: 'Protein',
  [ModelTypeConstants.EQUIPMENT]: 'Equipment',
  [ModelTypeConstants.OLIGO_CONJUGATE]: 'Oligo Conjugate',
  [ModelTypeConstants.OLIGO_DUPLEX]: 'Oligo Duplex',
  [ModelTypeConstants.CONTAINER]: 'Container',
  [ModelTypeConstants.PLATE]: 'Plate',
  [ModelTypeConstants.BOX]: 'Box',
  [ModelTypeConstants.LOCATION]: 'Location',
  [ModelTypeConstants.PROCESS]: 'Workflow Task Group',
  [ModelTypeConstants.PROCESS_TASK]: 'Workflow Task',
  [ModelTypeConstants.PROCESS_OUTPUT]: 'Workflow Output',
  [ModelTypeConstants.STAGE_ENTRY]: 'Stage Entry',
  [ModelTypeConstants.FOLDER]: 'Folder',
  [ModelTypeConstants.REQUEST]: 'Request',
  [ModelTypeConstants.REQUEST_V2_SUBMISSION]: 'Request',
  [ModelTypeConstants.STUDY]: 'Study',
  [ModelTypeConstants.PROTOCOL]: 'Protocol',
  [ModelTypeConstants.TEMPLATE]: 'Template',
  [ModelTypeConstants.TEMPLATE_COLLECTION]: 'Template Collection',
  [ModelTypeConstants.BULK_ASSEMBLY]: 'Assembly',
  [ModelTypeConstants.ASSAY_RUN]: 'Run',
  [ModelTypeConstants.DATASET]: 'Dataset',
  [ModelTypeConstants.FILE]: 'File',
  [ModelTypeConstants.ANALYSIS]: 'Analysis',
  [ModelTypeConstants.ANALYSIS_TEMPLATE]: 'Automation Template',
  [ModelTypeConstants.ANALYTICS_DASHBOARD]: 'Dashboard',
  [ModelTypeConstants.SEQUENCE_ANALYSIS]: 'DNA/RNA Alignment',
  [ModelTypeConstants.PROTEIN_ALIGNMENT]: 'AA Alignment',
  [ModelTypeConstants.CONNECTION]: 'Connection',
  [ModelTypeConstants.WORKLIST]: 'Worklist',
  [ModelTypeConstants.WORKSHEET]: 'Worksheet',
};

export function getObjectTypeDisplayName(apiId: string): string | undefined {
  const modelType = APIUtil.apiIdToMaybeType(apiId);
  return modelType ? getModelTypeDisplayName(modelType) : undefined;
}

// Falls back to the app-wide type name so types without an omnibox-specific label still read
// naturally (e.g. "Plate Design Template" rather than "plate_design_template").
export function getModelTypeDisplayName(modelType: string): string {
  return (
    MODEL_TYPE_DISPLAY_NAMES[modelType as ModelTypeConstants] ??
    typeToName(modelType).replace(/\b\w/g, (char) => char.toUpperCase())
  );
}
